/**
 * Formatage d'affichage (fr-FR), sans dépendance : Intl est fourni par le
 * navigateur, rien n'est téléchargé.
 */
import type { Dec } from '../lib/model';
import { formatAmount } from '../lib/money';
import { PAYMENT_MEANS_FR, TAX_CATEGORY_FR, UNIT_FR } from '../lib/labels.fr';

const DATE_FMT = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
const DATE_LONG_FMT = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });

/** ISO (AAAA-MM-JJ) → 05/03/2026. Toute autre forme est rendue telle quelle. */
export function formatDate(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return Number.isNaN(date.getTime()) ? iso : DATE_FMT.format(date);
}

export function formatDateLong(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return Number.isNaN(date.getTime()) ? iso : DATE_LONG_FMT.format(date);
}

export function formatMoney(value: Dec | undefined, currency = 'EUR'): string | undefined {
  return value ? formatAmount(value, currency) : undefined;
}

/** Quantité : 3 → « 3 », 1.5 → « 1,5 » (sans zéros inutiles). */
export function formatQuantity(value: Dec | undefined): string | undefined {
  if (!value) return undefined;
  const raw = value.toString();
  const trimmed = raw.includes('.') ? raw.replace(/0+$/, '').replace(/\.$/, '') : raw;
  return trimmed.replace('.', ',');
}

export function formatUnit(code: string | undefined): string | undefined {
  if (!code) return undefined;
  return UNIT_FR[code] ?? code;
}

export function formatTaxCategory(code: string | undefined): string | undefined {
  if (!code) return undefined;
  const label = TAX_CATEGORY_FR[code];
  return label ? `${label} (${code})` : code;
}

export function formatPaymentMeans(code: string | undefined): string | undefined {
  if (!code) return undefined;
  const label = PAYMENT_MEANS_FR[code];
  return label ? `${label} (${code})` : code;
}

/** « DE79 0000 0000 1234 5678 90 » — lisibilité IBAN, sans altérer la valeur. */
export function formatIban(iban: string | undefined): string | undefined {
  if (!iban) return undefined;
  const clean = iban.replace(/\s+/g, '').toUpperCase();
  if (!/^[A-Z0-9]{4,34}$/.test(clean)) return iban;
  return clean.replace(/(.{4})/g, '$1 ').trim();
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1).replace('.', ',')} ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`;
}
